import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';

import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { LostSaleService } from '../lost-sale/lost-sale.service';
import { LostSaleV2StateService } from './services/lost-sale-v2-state.service';
import { LostSaleTabType, LostSaleQueryParams, LostSaleFilters } from './models/lost-sale.interfaces';
import { LostSaleV2Export } from './exports/lost-sale-v2.export';
import { ExportDialogComponent, ExportFileFormat, ExportRange } from 'app/layout/export-overlay';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

// Filter Components
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { DatesFiltersComponent } from '../../filters/dates-filters/dates-filters.component';
import { ConfigurationFiltersComponent } from '../../filters/configuration-filters/configuration-filters.component';
import { RoutesFiltersComponent } from '../../filters/routes-filters/routes-filters.component';
import { BusinessPartnersFiltersComponent } from '../../filters/business-partners-filters/business-partners-filters.component';
import { MaterialsManagementFiltersComponent } from '../../filters/materials-management-filters/materials-management-filters.component';

// Tab Components will be loaded through router-outlet
import { HoverMenuDirective } from '@fuse/directives/hover-menu/hover-menu.directive';
import { MatSidenavModule } from '@angular/material/sidenav';
import { FilterSectionComponent } from 'app/common/components/filter-section/filter-section.component';

// Directives

@Component({
  selector: 'app-lost-sale-v2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatMenuModule,
    MatSelectModule,
    MatSnackBarModule,
    MatTabsModule,
    MatTooltipModule,
    MatDialogModule,
    FilterComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    MaterialsManagementFiltersComponent,
    HoverMenuDirective,
    MatSidenavModule,
    FilterSectionComponent
  ],
  providers: [LostSaleV2StateService, LostSaleV2Export],
  templateUrl: './lost-sale-v2.component.html',
  styleUrls: ['./lost-sale-v2.component.scss']
})
export class LostSaleV2Component implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Tab configuration
  selectedTabIndex = 0;
  LostSaleTabType = LostSaleTabType;

  // Loading state
  isLoading$;

  // Active tab
  activeTab$;

  // Filter form
  filterForm;

  // Check if there's data
  hasCommercialData = false;
  hasSummaryData = false;

  // Filter sidebar state
  filterOpened = false;

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  constructor(
    public stateService: LostSaleV2StateService,
    private lostSaleService: LostSaleService,
    private searchService: SearchService,
    private snackBar: MatSnackBar,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private exportService: LostSaleV2Export
  ) {
    // Initialize observables after stateService is available
    this.isLoading$ = this.stateService.loading$;
    this.activeTab$ = this.stateService.activeTab$;
    this.filterForm = this.stateService.filterForm;
  }

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  ngOnInit(): void {
    this.initializeSearch();
    this.subscribeToRoute();
    this.subscribeToDataChanges();
  }

  private initializeSearch(): void {
    // Subscribe to global search
    this.searchService.searchTerm$
      .pipe(takeUntil(this.destroy$))
      .subscribe(term => {
        this.stateService.setSearchTerm(term);
      });
  }

  private subscribeToRoute(): void {
    // Listen to navigation end events to sync tab selection
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateTabFromRoute(event.url);
      });

    // Set initial tab based on current route
    this.updateTabFromRoute(this.router.url);
  }

  private updateTabFromRoute(url: string): void {
    if (url.includes('/commercial')) {
      this.selectedTabIndex = 0;
      this.stateService.setActiveTab(LostSaleTabType.COMMERCIAL);
    } else if (url.includes('/summary')) {
      this.selectedTabIndex = 1;
      this.stateService.setActiveTab(LostSaleTabType.SUMMARY);
    }
  }

  private subscribeToDataChanges(): void {
    // Monitor commercial data
    this.stateService.commercialData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.hasCommercialData = data && data.length > 0;
      });

    // Monitor summary data
    this.stateService.summaryData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.hasSummaryData = data && data.length > 0;
      });
  }

  onTabChange(index: number): void {
    const route = index === 0 ? 'commercial' : 'summary';
    this.router.navigate([route], { relativeTo: this.activatedRoute, queryParamsHandling: 'preserve'});
  }

  onExport(): void {
    const activeTab = this.selectedTabIndex === 0 ? 'commercial' : 'summary';
    const title = activeTab === 'commercial' ? 'Venta Perdida Comercial' : 'Resumen Venta Perdida';

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      width: '500px',
      data: {
        title: `Exportar ${title}`,
        totalItems: activeTab === 'commercial'
          ? this.stateService['commercialTotalSubject'].value
          : this.stateService['summaryTotalSubject'].value
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.executeExport(result.format, result.range, activeTab);
      }
    });
  }

  private executeExport(format: ExportFileFormat, range: ExportRange, tabType: 'commercial' | 'summary'): void {
    const params: LostSaleQueryParams = {
      filters: this.stateService['filtersSubject'].value,
      search: this.stateService['searchTermSubject'].value,
      orderBy: tabType === 'commercial'
        ? this.stateService['commercialSortSubject'].value.active
        : this.stateService['summarySortSubject'].value.active,
      orderDirection: tabType === 'commercial'
        ? (this.stateService['commercialSortSubject'].value.direction === 'desc' ? 'DESC' : 'ASC')
        : (this.stateService['summarySortSubject'].value.direction === 'desc' ? 'DESC' : 'ASC')
    };

    if (tabType === 'commercial') {
      const pagination = this.stateService['commercialPageSubject'].value;
      const data = this.stateService['commercialDataSubject'].value;
      const total = this.stateService['commercialTotalSubject'].value;

      this.exportService.exportCommercial(
        format,
        range,
        total,
        pagination.pageSize,
        pagination.pageIndex,
        params,
        data
      );
    } else {
      const pagination = this.stateService['summaryPageSubject'].value;
      const data = this.stateService['summaryDataSubject'].value;
      const total = this.stateService['summaryTotalSubject'].value;

      this.exportService.exportSummary(
        format,
        range,
        total,
        pagination.pageSize,
        pagination.pageIndex,
        params,
        data
      );
    }

    // Show success message
    this.snackBar.open(
      'Exportación iniciada. El archivo se descargará automáticamente.',
      'Cerrar',
      {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['success-snackbar']
      }
    );
  }

  onFilter(): void {
    // Apply staged filters (move staged → applied + trigger API calls)
    this.stateService.applyFilters();
  }

  onClearFilter(): void {
    this.resetFilters();
  }

  resetFilters(): void {
    this.stateService.resetFilters();
    this.searchService.clear();
  }

  hasActiveFilters(): boolean {
    return this.stateService.hasActiveFilters();
  }

  hasData(): boolean {
    return this.selectedTabIndex === 0 ? this.hasCommercialData : this.hasSummaryData;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}